import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Button,
  Collapse,
  Divider,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { PaymentPlanEnum } from 'src/configs/APISchemas/BindPolicy';
import useDialog from 'src/helpers/hooks/useDialog';
import { PaymentFormValues } from 'src/pages/payment-msi/PaymentMsiV2/PaymentMsiV2';
import { CoverageStartField } from 'src/pages/payment-msi/PaymentMsiV2/components/CoverageStartField';
import { PaymentSidebarItem } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentSidebarItem';
import { PaymentSectionId } from 'src/pages/payment-msi/PaymentMsiV2/constants';
import {
  useStickyDesktopPaymentSidebar,
  useStickyTabPaymentSidebar,
} from 'src/pages/payment-msi/PaymentMsiV2/hooks/use-sticky-payment-sidebar.hook';
import { paymentBreakpoints } from 'src/pages/payment-msi/PaymentMsiV2/styles';
import {
  PaymentPlanOption,
  getBuildingAddressParts,
  getCoverageData,
  getDisplayedInterestedParty,
  getPolicyDate,
} from 'src/pages/payment-msi/PaymentMsiV2/utils';
import { getPurchasePolicyInput } from 'src/redux/selectors/purchasePolicy';
import { AppTheme } from 'src/themes/muiTheme';

interface PaymentSidebarData {
  currentPlanPrice: string | undefined;
  currentPlanPeriod: string;
  policyStartDate: string | null;
  renterName: string;
  buildingAddress: {
    addressPart1: string;
    addressPart2: string;
  };
  buildingUnit: string | undefined;
  coveragesData: {
    label: string;
    value: string;
  }[];
  interestedParty: string;
  additionalInsureds: string[];
}

const sharedPaymentSidebarStyles = {
  contentRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  policyPriceText: {
    color: '#3DB157',
  },
};

interface DesktopPaymentSidebarProps {
  data: PaymentSidebarData;
}
const desktopPaymentSidebarStyles = {
  root: {
    padding: '24px',
    background: '#fff',
    borderRadius: '24px',
    boxShadow: '4px 16px 32px 0px rgba(29, 29, 29, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '24px',
  },
  policyPriceWrapper: (theme: AppTheme) => {
    const maxSmallDesktopSize = paymentBreakpoints.smallDesktop;
    const minLargeDesktopSize = theme.breakpoints.values.lg + 1;
    const smallDesktop = theme.breakpoints.between(
      minLargeDesktopSize,
      maxSmallDesktopSize,
    );

    return {
      ...sharedPaymentSidebarStyles.contentRow,
      [smallDesktop]: {
        alignItems: 'center',
      },
    };
  },
  policyPrice: (theme: AppTheme) => {
    const maxSmallDesktopSize = paymentBreakpoints.smallDesktop;
    const minLargeDesktopSize = theme.breakpoints.values.lg + 1;
    const smallDesktop = theme.breakpoints.between(
      minLargeDesktopSize,
      maxSmallDesktopSize,
    );

    return {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
      columnGap: '8px',
      [smallDesktop]: {
        flexDirection: 'column',
      },
    };
  },
};
function DesktopPaymentSidebar({ data }: DesktopPaymentSidebarProps) {
  const { parentRef, sidebarRef } = useStickyDesktopPaymentSidebar();
  const {
    currentPlanPrice,
    currentPlanPeriod,
    policyStartDate,
    renterName,
    buildingAddress,
    coveragesData,
    interestedParty,
    additionalInsureds,
    buildingUnit,
  } = data;

  return (
    <Box component={'section'} ref={parentRef}>
      <Box
        sx={desktopPaymentSidebarStyles.root}
        component={'aside'}
        ref={sidebarRef}
      >
        <Box
          sx={desktopPaymentSidebarStyles.policyPriceWrapper}
          alignItems={'flex-end'}
        >
          <Typography component={'h3'} variant={'h4'}>
            Your policy
          </Typography>
          <Box sx={desktopPaymentSidebarStyles.policyPrice}>
            <Typography
              sx={sharedPaymentSidebarStyles.policyPriceText}
              component={'p'}
              variant={'h3'}
            >
              {currentPlanPrice}
            </Typography>
            <Typography component={'p'} variant={'body2'}>
              / {currentPlanPeriod}
            </Typography>
          </Box>
        </Box>
        <PaymentSidebarItem label={'Start date'} value={policyStartDate} />
        <Divider />
        <Box>
          <PaymentSidebarItem label={'Name'} value={renterName} />
          <PaymentSidebarItem
            label={'Address'}
            value={
              <>
                {buildingAddress.addressPart1}
                <br />
                {buildingAddress.addressPart2}
              </>
            }
          />
          {buildingUnit ? (
            <PaymentSidebarItem label={'Unit'} value={buildingUnit} />
          ) : null}
        </Box>
        <Divider />
        <Box>
          {coveragesData.map((coverage) => {
            return (
              <PaymentSidebarItem
                key={coverage.label}
                label={coverage.label}
                value={coverage.value}
              />
            );
          })}
        </Box>
        <Divider />
        <Box>
          <PaymentSidebarItem
            label={'Interested party'}
            value={interestedParty}
          />
          <PaymentSidebarItem
            label={'Additional insured'}
            value={
              additionalInsureds.length > 0
                ? additionalInsureds.map((name, index) => {
                    return (
                      <Typography
                        key={`${name}-${index}`}
                        display={'block'}
                        component={'span'}
                      >
                        {name}
                      </Typography>
                    );
                  })
                : '-'
            }
          />
        </Box>
        <Box>
          <CoverageStartField />
        </Box>
      </Box>
    </Box>
  );
}

interface TabPaymentSidebarProps {
  data: PaymentSidebarData;
}
const tabPaymentSidebarStyles = {
  root: {
    padding: '24px',
    boxShadow: '4px 16px 32px 0px rgba(29, 29, 29, 0.08)',
    background: '#fff',
    borderRadius: '0px 0px 24px 24px',
    position: 'sticky',
    zIndex: 1,
  },
  policyPriceWrapper: {
    ...sharedPaymentSidebarStyles.contentRow,
    alignItems: 'center',
  },
  policyPriceContent: (theme: AppTheme) => {
    const smallMobileMaxSize = paymentBreakpoints.smallMobile;
    const smallMobile = theme.breakpoints.down(smallMobileMaxSize);

    return {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
      [smallMobile]: {
        flexDirection: 'column',
      },
    };
  },
  collapseButton: (theme: AppTheme) => {
    return {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      border: `1px solid ${theme.palette.text.primary}`,
      transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    };
  },
  policyData: {
    paddingTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
  },
};
function TabPaymentSidebar({ data }: TabPaymentSidebarProps) {
  const {
    currentPlanPrice,
    currentPlanPeriod,
    coveragesData,
    interestedParty,
    additionalInsureds,
  } = data;

  const {
    handleToggle: toggleCollapse,
    handleClose: closeCollapse,
    open: isCollapsed,
  } = useDialog();

  const { sidebarRef } = useStickyTabPaymentSidebar();

  function onGoToPayment() {
    closeCollapse();
    const paymentOptionSection = document.getElementById(
      PaymentSectionId.PaymentOption,
    );

    if (!paymentOptionSection) {
      return;
    }

    const elementOffsetTop = paymentOptionSection.offsetTop;
    const topOffset = window.innerHeight * 0.5;
    const elementPosition = elementOffsetTop - topOffset;

    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth',
    });
  }

  return (
    <Box component={'aside'} ref={sidebarRef} sx={tabPaymentSidebarStyles.root}>
      <Box component={'header'} sx={tabPaymentSidebarStyles.policyPriceWrapper}>
        <Typography component={'h3'} variant={'h4'}>
          Your policy
        </Typography>
        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          columnGap={'8px'}
        >
          <Box sx={tabPaymentSidebarStyles.policyPriceContent}>
            <Typography
              sx={sharedPaymentSidebarStyles.policyPriceText}
              component={'p'}
              variant={'h3'}
            >
              {currentPlanPrice}
            </Typography>
            <Typography component={'p'} variant={'body2'}>
              / {currentPlanPeriod}
            </Typography>
          </Box>
          <IconButton
            sx={tabPaymentSidebarStyles.collapseButton}
            onClick={toggleCollapse}
          >
            <KeyboardArrowDownIcon
              sx={{
                transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={isCollapsed} timeout={'auto'}>
        <Box sx={tabPaymentSidebarStyles.policyData}>
          <Divider />
          <Box>
            {coveragesData.map((coverage) => {
              return (
                <PaymentSidebarItem
                  key={coverage.label}
                  label={coverage.label}
                  value={coverage.value}
                />
              );
            })}
          </Box>
          <Divider />
          <Box>
            <PaymentSidebarItem
              label={'Interested party'}
              value={interestedParty}
            />
            <PaymentSidebarItem
              label={'Additional insureds'}
              value={
                additionalInsureds.length > 0
                  ? additionalInsureds.map((name, index) => {
                      return (
                        <Typography
                          key={`${name}-${index}`}
                          display={'block'}
                          component={'span'}
                        >
                          {name}
                        </Typography>
                      );
                    })
                  : '-'
              }
            />
          </Box>
          <CoverageStartField />
          <Button
            onClick={onGoToPayment}
            variant={'contained'}
            color={'primary'}
            fullWidth
          >
            Go To Payment
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

interface PaymentSidebarProps {
  paymentPlans: PaymentPlanOption[];
}
export function PaymentSidebar({ paymentPlans }: PaymentSidebarProps) {
  const isTab = useMediaQuery<AppTheme>((theme) => {
    const tabletMaxSize = theme.breakpoints.values.lg + 1;
    return theme.breakpoints.down(tabletMaxSize);
  });

  const { watch } = useFormContext<PaymentFormValues>();

  const purchasePolicyInput = useSelector(getPurchasePolicyInput);

  const currentPaymentPlan = watch('paymentPlan');
  const currentPlanOption = paymentPlans.find(
    (plan) => plan.value === currentPaymentPlan,
  );
  const currentPlanPrice = currentPlanOption?.price;
  const currentPlanPeriod =
    currentPaymentPlan === PaymentPlanEnum.Annual ? 'year' : 'month';
  const policyStartDate = getPolicyDate(purchasePolicyInput.coverageStart);
  const renterName = `${purchasePolicyInput.firstName} ${purchasePolicyInput.lastName}`;
  const buildingAddress = getBuildingAddressParts({
    address: purchasePolicyInput.address,
    buildingAddress: purchasePolicyInput.buildingAddress,
  });
  const additionalInsureds = purchasePolicyInput.additionalInsuredRoommates.map(
    (insured) => `${insured.firstName} ${insured.lastName}`.trim(),
  );
  const interestedParty = getDisplayedInterestedParty(
    purchasePolicyInput.additionalInterests?.[0],
  );

  const coveragesData = getCoverageData(purchasePolicyInput);

  const data: PaymentSidebarData = {
    additionalInsureds,
    buildingAddress,
    buildingUnit: purchasePolicyInput.buildingUnit,
    coveragesData,
    currentPlanPeriod,
    currentPlanPrice,
    interestedParty,
    policyStartDate,
    renterName,
  };

  return isTab ? (
    <TabPaymentSidebar data={data} />
  ) : (
    <DesktopPaymentSidebar data={data} />
  );
}
